import { Link } from '@readium/shared';
import { dirFromPath, findCssInCacheWithKey, resolveCssImports } from './cssInliner.js';
import { rewriteFontUrlsInText } from './fontDeobfuscator.js';
import { adaptPublisherCss } from './publisherCssCompat.js';
import { sendBreadcrumb } from './sentryBreadcrumb.js';

/**
 * On-demand CSS cache for the Readium 2 pipeline.
 *
 * Replaces the eager `preFetchCssFiles` bulk decrypt. Each entry is a
 * Promise<string> so concurrent requests for the same href share a single
 * fetch+decrypt+process pipeline.
 *
 * The cached string is the *processed* CSS:
 *   1. Decrypted via the fetcher (DecryptingResource pipeline).
 *   2. Publisher CSS adapted to the paginated multicol layout (forced page
 *      breaks doubled as column breaks, `-epub-` properties unprefixed).
 *   3. IDPF-deobfuscated font url(...) references rewritten to blob URLs.
 *   4. @import statements recursively resolved against this same cache.
 *
 * The cached string is NOT rebased to any specific xhtml directory — the
 * caller (DecryptingResource.inlineCssInHtml) rebases per-document.
 */
export class LazyCssCache {
    /**
     * @param {Object} fetcher — the decrypting publication fetcher
     * @param {Array<{href: string}>} resourceList — publication.resources.items
     * @param {() => Map<string,string>} getFontBlobUrls — IDPF map accessor.
     *        EpubViewer awaits IDPF deobfuscation before priming the cache,
     *        so this accessor returns the fully-populated map on first miss.
     *        Kept as a function (not a direct Map) so the cache reads the
     *        latest reference if the owner ever swaps it.
     */
    constructor(fetcher, resourceList, getFontBlobUrls) {
        this.fetcher = fetcher;
        this.getFontBlobUrls = getFontBlobUrls;
        /**
         * Probe Map keyed by every publication href, with empty-string values.
         * Reused by `_resolveKey` to leverage `findCssInCacheWithKey`'s matching
         * logic (direct / normalized / suffix / filename) without rebuilding
         * the Map on every <link> resolution. The values are unused by
         * `_resolveKey` — only the keys are inspected.
         */
        this._resourceProbe = new Map(resourceList.map(r => [r.href, '']));
        /** @type {Map<string, Promise<string>>} keyed by publication-absolute href */
        this._cache = new Map();
    }

    /**
     * Resolve a (possibly relative) href into the publication-absolute key
     * used inside the cache. Returns null when the href cannot be matched
     * to any resource entry.
     */
    _resolveKey(relativeHref, requesterDir) {
        const result = findCssInCacheWithKey(this._resourceProbe, requesterDir, relativeHref);
        return result ? result.cacheKey : null;
    }

    /**
     * Returns { css, cacheKey } where css is fully processed (but not
     * rebased to the requester's directory). Returns null when the resource
     * is not part of the publication or fetching fails.
     *
     * @param {string} relativeHref — href as it appears in <link> or @import
     * @param {string} requesterDir — directory of the requesting document/CSS
     * @param {Set<string>|null} visited — set of publication-absolute paths
     *        currently being processed up the @import chain. When the resolved
     *        cacheKey is already present, returning the in-flight Promise
     *        would deadlock (we would await our own ancestor), so a
     *        `{ cyclic: true }` sentinel is returned instead. Callers must
     *        treat that as a cycle and drop the offending @import.
     */
    async get(relativeHref, requesterDir, visited = null) {
        const cacheKey = this._resolveKey(relativeHref, requesterDir);
        if (!cacheKey) return null;

        // Cross-entry cycle guard: the per-`_loadAndProcess` `visited` set
        // protects in-process recursion within a single resolveCssImports
        // call, but does not span sibling cache entries. Without this check,
        // mutual @imports (A → B → A) deadlock: get("A") awaits Promise_A,
        // which awaits Promise_B, which re-enters get("A") and awaits the
        // unsettled Promise_A.
        if (visited?.has(cacheKey)) return { css: '', cacheKey, cyclic: true };

        if (!this._cache.has(cacheKey)) {
            this._cache.set(cacheKey, this._loadAndProcess(cacheKey, visited));
        }
        try {
            const css = await this._cache.get(cacheKey);
            return css === null ? null : { css, cacheKey };
        } catch {
            // _loadAndProcess returns null on fetch errors rather than throwing,
            // so reaching this catch means a downstream step threw unexpectedly
            // (e.g. a future @import resolver bug). Cache the rejection so we
            // do not retry-storm on the same broken resource.
            return null;
        }
    }

    /**
     * @param {string} cacheKey — publication-absolute href
     * @param {Set<string>|null} parentVisited — keys already in the @import
     *        chain when this entry was first requested. Used so child
     *        `get()` calls can detect cross-entry cycles back to any
     *        ancestor, not just the immediate parent.
     * @returns {Promise<string|null>}
     */
    async _loadAndProcess(cacheKey, parentVisited = null) {
        let raw;
        try {
            raw = await this.fetcher.get(new Link({ href: cacheKey })).readAsString();
        } catch (e) {
            sendBreadcrumb(
                'fetch',
                'lazy_css_fetch_failed',
                {
                    href: cacheKey,
                    errorMessage: e?.message,
                },
                'warning'
            );
            return null;
        }
        if (raw == null) return null;

        const fontBlobUrls = this.getFontBlobUrls?.() ?? null;
        // First, so each stylesheet is adapted once in its own cache entry;
        // imported sheets arrive below already adapted.
        let processed = adaptPublisherCss(raw);
        if (fontBlobUrls && fontBlobUrls.size > 0) {
            processed = rewriteFontUrlsInText(processed, fontBlobUrls, dirFromPath(cacheKey));
        }

        const visited = new Set(parentVisited ?? []);
        visited.add(cacheKey);
        processed = await resolveCssImports(
            processed,
            cacheKey,
            (relHref, requesterDir) => this.get(relHref, requesterDir, visited),
            visited
        );

        return processed;
    }
}
